// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// View models + mock fallback for the operational screens (Обзор / Очередь /
// Календарь / Каналы) of Бриф. Real data comes from the v2-promotion-publisher
// worker (see api.ts); when it is unreachable (local dev, expired session) the
// screens fall back to this mock so the console always demonstrates.

export type Platform =
  | 'telegram'
  | 'twitter'
  | 'instagram'
  | 'threads'
  | 'facebook'
  | 'linkedin'
  | 'youtube'
  | 'tiktok'
  | 'reddit'
  | 'producthunt';

export type PlatformStatus = 'pending' | 'publishing' | 'published' | 'failed' | 'skipped';

export type QueueStage = 'attention' | 'scheduled' | 'publishing' | 'published';

export interface PostPlatform {
  platform: Platform;
  status: PlatformStatus;
  link?: string;
}

export interface PromoPost {
  id: string;
  title: string;
  stage: QueueStage;
  platforms: PostPlatform[];
  scheduledAt: string | null; // ISO
  createdAt: string | null; // ISO
}

export type ChannelReadiness = 'ready' | 'setup' | 'manual';

export interface ChannelInfo {
  platform: Platform;
  readiness: ChannelReadiness;
  via: 'official' | 'buffer' | 'cookies' | 'manual';
  detail: string;
}

export interface OverviewData {
  attention: number;
  scheduled: number;
  publishing: number;
  channelsReady: number;
  channelsTotal: number;
  recent: PromoPost[];
}

// ── Platform display ─────────────────────────────────────────────

export const PLATFORM_LABEL: Record<Platform, string> = {
  telegram: 'Telegram',
  twitter: 'X.com',
  instagram: 'Instagram',
  threads: 'Threads',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  producthunt: 'Product Hunt',
};

export const PLATFORM_GLYPH: Record<Platform, string> = {
  telegram: 'TG',
  twitter: '𝕏',
  instagram: 'IG',
  threads: '@',
  facebook: 'f',
  linkedin: 'in',
  youtube: 'YT',
  tiktok: 'TT',
  reddit: 'r/',
  producthunt: 'P',
};

export const STATUS_DOT: Record<PlatformStatus, string> = {
  pending: '#8a8a85',
  publishing: '#b08968',
  published: '#1a1916',
  failed: '#c1442e',
  skipped: '#cfc9c0',
};

export const STAGE_META: Record<QueueStage, { label: string; hint: string; tone: string }> = {
  attention: { label: 'Нужно действие', hint: 'Ошибки и черновики', tone: '#c1442e' },
  scheduled: { label: 'Запланировано', hint: 'Ждут времени публикации', tone: '#b08968' },
  publishing: { label: 'В работе', hint: 'Публикуются сейчас', tone: '#5f5c55' },
  published: { label: 'Опубликовано', hint: 'Готовые посты', tone: '#1a1916' },
};

export const READINESS_META: Record<ChannelReadiness, { label: string; tone: string }> = {
  ready: { label: 'Готов', tone: '#1a1916' },
  setup: { label: 'Нужна настройка', tone: '#b08968' },
  manual: { label: 'Только вручную', tone: '#8a8a85' },
};

export function deriveOverview(posts: PromoPost[], channels: ChannelInfo[]): OverviewData {
  return {
    attention: posts.filter((p) => p.stage === 'attention').length,
    scheduled: posts.filter((p) => p.stage === 'scheduled').length,
    publishing: posts.filter((p) => p.stage === 'publishing').length,
    channelsReady: channels.filter((c) => c.readiness === 'ready').length,
    channelsTotal: channels.length,
    recent: posts.slice(0, 5),
  };
}

// Group posts into the calendar week (Mon..Sun) by scheduledAt.
export const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export interface CalendarCell {
  day: string;
  date: string;
  posts: PromoPost[];
}

export function buildCalendar(posts: PromoPost[]): CalendarCell[] {
  // Anchor to the Monday of the week containing the earliest scheduled post,
  // or 01.07 mock week when nothing is scheduled.
  const scheduled = posts.filter((p) => p.scheduledAt);
  const base = scheduled.length ? new Date(scheduled[0].scheduledAt as string) : new Date('2026-07-01T00:00:00Z');
  const monday = new Date(base);
  const dow = (monday.getUTCDay() + 6) % 7; // 0 = Monday
  monday.setUTCDate(monday.getUTCDate() - dow);

  return WEEKDAYS.map((day, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dateKey = `${d.getUTCFullYear()}-${mm}-${dd}`;
    const dayPosts = scheduled.filter((p) => (p.scheduledAt as string).slice(0, 10) === dateKey);
    return { day, date: `${dd}.${mm}`, posts: dayPosts };
  });
}
